import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Alert, Modal, TouchableOpacity } from 'react-native';
import { Text, Card, Avatar, useTheme, ActivityIndicator, Button, Divider, Chip, List, Snackbar, IconButton } from 'react-native-paper';
import { getChildById, startVisit, endVisit, deleteChild } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const ChildDetailScreen = ({ route, navigation }) => {
  const theme = useTheme();
  const { childId } = route.params || {};
  
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchChildDetails = async () => {
    try {
      setLoading(true);
      
      if (!childId) {
        setSnackbarMessage('Geçersiz çocuk ID');
        setSnackbarVisible(true);
        navigation.goBack();
        return;
      }
      
      const response = await getChildById(childId);
      
      if (response.success) {
        const childData = {
          ...response.data,
          name: `${response.data.first_name} ${response.data.last_name}`,
          birth_date: response.data.birthdate
        };
        setChild(childData);
      } else {
        setSnackbarMessage(response.message || 'Çocuk bilgileri alınırken bir hata oluştu');
        setSnackbarVisible(true);
        setTimeout(() => navigation.goBack(), 2000);
      }
    } catch (error) {
      console.error('Error fetching child details:', error);
      setSnackbarMessage(error.message || 'Çocuk bilgileri alınırken bir hata oluştu');
      setSnackbarVisible(true);
      setTimeout(() => navigation.goBack(), 2000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChildDetails();
  }, [childId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const padZero = (num) => num.toString().padStart(2, '0');
      
      return `${padZero(date.getDate())}.${padZero(date.getMonth() + 1)}.${date.getFullYear()} ${padZero(date.getHours())}:${padZero(date.getMinutes())}`;
    } catch (error) {
      return dateString;
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    try {
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return `${age} yaş`;
    } catch (error) {
      return '-';
    }
  };

  const handleCreateVisit = () => {
    Alert.alert(
      'Ziyaret Başlat',
      `${child.name} için yeni bir ziyaret başlatmak istiyor musunuz?`,
      [
        { text: 'İptal', style: 'cancel' },
        { 
          text: 'Başlat', 
          onPress: async () => {
            try {
              setActionLoading(true);
              const response = await startVisit(child.id, '');
              
              if (response.success) {
                setSnackbarMessage(`${child.name} için ziyaret başlatıldı`);
                setSnackbarVisible(true);
                // Detayları yenile
                fetchChildDetails();
              } else {
                setSnackbarMessage(response.message || 'Ziyaret başlatılırken bir hata oluştu');
                setSnackbarVisible(true);
              }
            } catch (error) {
              console.error('Error starting visit:', error);
              setSnackbarMessage(error.message || 'Ziyaret başlatılırken bir hata oluştu');
              setSnackbarVisible(true);
            } finally {
              setActionLoading(false);
            }
          }
        },
      ]
    );
  };

  const handleEndVisit = (visitId) => {
    Alert.alert(
      'Ziyaret Sonlandır',
      `${child.name} için ziyareti sonlandırmak istiyor musunuz?`,
      [
        { text: 'İptal', style: 'cancel' },
        { 
          text: 'Sonlandır', 
          onPress: async () => {
            try {
              setActionLoading(true);
              const response = await endVisit(visitId, '');
              
              if (response.success) {
                setSnackbarMessage(`${child.name} için ziyaret sonlandırıldı`);
                setSnackbarVisible(true);
                // Detayları yenile
                fetchChildDetails();
              } else {
                setSnackbarMessage(response.message || 'Ziyaret sonlandırılırken bir hata oluştu');
                setSnackbarVisible(true);
              }
            } catch (error) {
              console.error('Error ending visit:', error);
              setSnackbarMessage(error.message || 'Ziyaret sonlandırılırken bir hata oluştu');
              setSnackbarVisible(true);
            } finally {
              setActionLoading(false);
            }
          }
        },
      ]
    );
  };

  // Çocuğu silmeyi onayla
  const confirmDelete = () => {
    // Önce aktif ziyaret kontrollerini yap
    //console.log('Çocuk silinmek isteniyor, aktif ziyaret kontrolü yapılıyor:', child);
    
    // Aktif ziyaret kontrolü - backend'den gelen yapıya göre farklı alanlarda olabilir
    if (child.active_visit || 
        child.active_visits?.length > 0 || 
        child.visits?.some(visit => visit.status === 'Active' || visit.end_time === null)) {
      setErrorMessage(`${child.name} şu anda parkta olduğu için silinemez. Önce aktif ziyareti sonlandırmalısınız.`);
      setErrorModalVisible(true);
      return;
    }
    
    // Aktif ziyaret yoksa silme modalini göster
    setDeleteConfirmVisible(true);
  };
  
  // Silme modalını kapat
  const cancelDelete = () => {
    setDeleteConfirmVisible(false);
  };
  
  // Hata modalını kapat
  const closeErrorModal = () => {
    setErrorModalVisible(false);
  };
  
  // Aktif ziyaretler sayfasına git
  const goToActiveVisits = () => {
    setErrorModalVisible(false);
    // URL'den emin olduğumuz rotülama: /VisitsManagement/ActiveVisits
    navigation.navigate('VisitsManagement', {
      screen: 'ActiveVisits'
    });
  };

  // Çocuğu sil
  const handleDeleteChild = async () => {
    try {
      setActionLoading(true);
      setDeleteConfirmVisible(false);
      
      const response = await deleteChild(child.id);
      
      if (response.success) {
        setSnackbarMessage(`${child.name} başarıyla silindi`);
        setSnackbarVisible(true);
        
        // Başarılı ise 1 saniye sonra listeye dön
        setTimeout(() => {
          navigation.goBack();
        }, 1000);
      } else {
        if (response.statusCode === 400 && response.message?.includes('active')) {
          setErrorMessage(`${child.name} adlı çocuk aktif bir ziyarete sahip olduğu için silinemez.`);
          setErrorModalVisible(true);
        } else {
          setSnackbarMessage(response.message || `${child.name} silinirken bir hata oluştu`);
          setSnackbarVisible(true);
        }
      }
    } catch (error) {
      console.error('Error deleting child:', error);
      if (error.response?.status === 400 && error.response?.data?.message?.includes('active')) {
        setErrorMessage(`${child.name} adlı çocuk aktif bir ziyarete sahip olduğu için silinemez.`);
        setErrorModalVisible(true);
      } else {
        setSnackbarMessage(error.message || `${child.name} silinirken bir hata oluştu`);
        setSnackbarVisible(true);
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
        <Text style={styles.loadingText}>Çocuk bilgileri yükleniyor...</Text>
      </View>
    );
  }

  if (!child) {
    return (
      <View style={styles.errorContainer}>
        <MaterialCommunityIcons name="account-alert" size={80} color="#BDBDBD" />
        <Text style={styles.errorText}>Çocuk bilgileri bulunamadı</Text>
        <Button mode="contained" onPress={() => navigation.goBack()}>
          Geri Dön
        </Button>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.headerCard}>
        <Card.Content style={styles.headerContent}>
          <View style={styles.avatarContainer}>
            <Avatar.Icon 
              size={80} 
              icon={child.gender === 'male' ? "human-male-boy" : "human-female-girl"} 
              color="white"
              style={{ backgroundColor: 'rgb(125, 76, 236)' }}
            />
            <View style={styles.statusContainer}>
              <Chip 
                icon={child.active_visit ? "timer-sand" : "timer-off"}
                style={[styles.statusChip, {backgroundColor: child.active_visit ? 'rgb(125, 76, 236)' : '#9E9E9E'}]} 
                textStyle={{color: 'white'}}
              >
                {child.active_visit ? 'AKTİF ZİYARET' : 'İÇERİDE DEĞİL'}
              </Chip>
            </View>
          </View>
          
          <View style={styles.childNameContainer}>
            <Text style={styles.childName}>{child.name}</Text>
            <Text style={styles.childAge}>{calculateAge(child.birth_date)}</Text>
            
            <Chip 
              icon={child.gender === 'male' ? "gender-male" : "gender-female"} 
              style={[styles.genderChip, {backgroundColor: 'rgba(125, 76, 236, 0.2)'}]}
              textStyle={{color: 'rgb(125, 76, 236)'}}  
            >
              {child.gender === 'male' ? 'Erkek' : 'Kız'}
            </Chip>
          </View>
        </Card.Content>
        
        <Card.Actions style={styles.actionButtonsContainer}>
          
          <Button 
            style={[styles.actionButton, styles.deleteButton]}
            icon="delete"
            mode="contained"
            onPress={confirmDelete}
            disabled={actionLoading}
            color="#fff"
          >
            Sil
          </Button>
        </Card.Actions>
      </Card>
      
      <Card style={styles.infoCard}>
        <Card.Title title="Kişisel Bilgiler" titleStyle={{color: 'rgb(125, 76, 236)'}} />
        <Card.Content>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Doğum Tarihi:</Text>
            <Text style={styles.infoValue}>{formatDate(child.birth_date)}</Text>
          </View>
          <Divider style={styles.divider} />
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Toplam Ziyaret:</Text>
            <Text style={styles.infoValue}>{child.visit_count || 0}</Text>
          </View>
          <Divider style={styles.divider} />
        </Card.Content>
      </Card>
      
      <Card style={styles.infoCard}>
        <Card.Title title="Veli Bilgileri" titleStyle={{color: 'rgb(125, 76, 236)'}} />
        <Card.Content>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Veli:</Text>
            <Text style={styles.infoValue}>{child.parent?.parent_name || child.parent_name || '-'}</Text>
          </View>
          <Divider style={styles.divider} />
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Telefon:</Text>
            <Text style={styles.infoValue}>{child.parent?.phone || child.parent_phone || '-'}</Text>
          </View>
          <Divider style={styles.divider} />
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>E-posta:</Text>
            <Text style={styles.infoValue}>{child.parent?.email || child.parent_email || '-'}</Text>
          </View>
          <Divider style={styles.divider} />
        </Card.Content>
      </Card>
      
      <Card style={styles.infoCard}>
        <Card.Title title="Notlar" titleStyle={{color: 'rgb(125, 76, 236)'}} />
        <Card.Content>
          <View style={styles.notesContainer}>
            <Text style={styles.infoLabel}>Özel Notlar:</Text>
            <Text style={styles.notesText}>{child.notes || 'Belirtilmemiş'}</Text>
          </View>
        </Card.Content>
      </Card>
      
      <Card style={styles.infoCard}>
        <Card.Title 
          title="Son Aktiviteler" 
          right={(props) => 
            <Button 
              mode="text" 
              onPress={() => navigation.navigate('VisitHistory', { childId: child.id })}
            >
              Tümü
            </Button>
          }
        />
        <Card.Content>
          {(child.activities && child.activities.length > 0) ? (
            <List.Section>
              {child.activities.slice(0, 5).map((activity, index) => (
                <View key={activity.id || index}>
                  <List.Item
                    title={activity.activityType?.name || 'Ziyaret'}
                    description={`${formatDateTime(activity.start_time)} - ${activity.end_time ? formatDateTime(activity.end_time) : 'Devam Ediyor'}`}
                    left={props => (
                      <List.Icon {...props} icon="calendar-check" color={theme.colors.primary} />
                    )}
                  />
                  {index < child.activities.length - 1 && <Divider />}
                </View>
              ))}
            </List.Section>
          ) : (
            <Text style={styles.emptyText}>Henüz aktivite kaydı bulunmamaktadır</Text>
          )}
        </Card.Content>
      </Card>
      
      {/* Özel yerleşimli ve stilli Snackbar */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
      >
        {snackbarMessage}
      </Snackbar>
      
      {/* Silme Onay Modalı */}
      <Modal
        visible={deleteConfirmVisible}
        transparent={true}
        onRequestClose={cancelDelete}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.iconContainer}>
              <MaterialCommunityIcons 
                name="alert-circle-outline" 
                size={60} 
                color="#FF9800" 
                style={styles.warningIcon}
              />
            </View>
            
            <Text style={styles.modalTitle}>Çocuk Profilini Sil</Text>
            <Text style={styles.modalText}>
              {child?.name} adlı çocuğu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </Text>
            
            <View style={styles.modalFooter}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.singleButton]}
                onPress={handleDeleteChild}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text style={styles.buttonText}>Evet, Sil</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalButton, styles.closeButton]}
                onPress={cancelDelete}
              >
                <Text style={styles.closeButtonText}>İptal</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Hata Modalı */}
      <Modal
        visible={errorModalVisible}
        transparent={true}
        onRequestClose={closeErrorModal}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <MaterialCommunityIcons 
              name="alert-circle-outline" 
              size={60} 
              color="#FF9800" 
              style={styles.warningIcon}
            />
            
            <Text style={styles.modalTitle}>Silme İşlemi Başarısız</Text>
            <Text style={styles.modalText}>{errorMessage}</Text>
            
            <View style={styles.modalFooter}>
              {errorMessage.includes('aktif') && (
                <TouchableOpacity 
                  style={[styles.modalButton, styles.singleButton]}
                  onPress={goToActiveVisits}
                >
                  <Text style={styles.buttonText}>AKTİF ZİYARETLERİ GÖR</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity 
                style={[styles.modalButton, styles.closeButton]}
                onPress={closeErrorModal}
              >
                <Text style={{color: 'rgb(125, 76, 236)', fontWeight: '600', fontSize: 16, textTransform: 'uppercase'}}>KAPAT</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgb(232, 162, 249)',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#757575',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    marginVertical: 20,
    fontSize: 16,
    color: '#757575',
    textAlign: 'center',
  },
  headerCard: {
    marginVertical: 10,
    marginHorizontal: 10,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  headerContent: {
    padding: 16,
  },
  avatarContainer: {
    alignItems: 'center',
    marginBottom: 20,
    position: 'relative',
  },
  statusContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  statusChip: {
    height: 30,
  },
  childNameContainer: {
    alignItems: 'center',
  },
  childName: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  childAge: {
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
    marginBottom: 10,
  },
  genderChip: {
    marginBottom: 10,
  },
  actionButtonsContainer: {
    padding: 16,
    paddingTop: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  actionButton: {
    height: 50,
    justifyContent: 'center',
    flex: 1,
    marginHorizontal: 5,
  },
  createButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  editButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  deleteButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  infoCard: {
    margin: 10,
    marginTop: 0,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  infoLabel: {
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
    flex: 2,
  },
  infoValue: {
    fontSize: 16,
    fontWeight: '500',
    flex: 3,
    textAlign: 'right',
  },
  divider: {
    marginVertical: 8,
  },
  notesContainer: {
    paddingVertical: 8,
  },
  notesText: {
    fontSize: 16,
    marginTop: 5,
    color: '#212121',
  },
  activityButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 10,
  },
  emptyText: {
    fontSize: 16,
    color: '#9E9E9E',
    textAlign: 'center',
    marginVertical: 20,
  },
  // Modal Stilleri - Görseldeki tasarım
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 8,
    width: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    paddingTop: 25,
    paddingBottom: 10,
    borderColor: 'rgb(125, 76, 236)',
    borderWidth: 1,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
  },
  modalBody: {
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  iconContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  warningIcon: {
    marginBottom: 16,
    color: 'rgb(125, 76, 236)',
  },
  modalText: {
    fontSize: 16,
    color: '#424242',
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalFooter: {
    padding: 10,
  },
  modalButton: {
    borderRadius: 4,
    paddingVertical: 12,
    marginVertical: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  singleButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  closeButton: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: 'rgb(125, 76, 236)',
  },
  buttonText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 16,
    textTransform: 'uppercase',
  },
  closeButtonText: {
    color: 'rgb(125, 76, 236)',
    fontWeight: '600',
    fontSize: 16,
    textTransform: 'uppercase',
  },
  // Geriye dönük uyumluluk için bırakılan stiller
  modalMessage: {
    fontSize: 16,
    color: '#424242',
  },
  modalButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modalActionButton: {
    minWidth: 180,
  },
  
  // Özel Snackbar Stilleri
  customSnackbarContainer: {
    position: 'absolute',
    top: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 1000,
  },
  customSnackbar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgb(125, 76, 236)',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 4,
    width: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  snackbarText: {
    color: 'white',
    flex: 1,
    fontSize: 16,
  },
  snackbarButton: {
    color: 'white',
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

export default ChildDetailScreen;
